import { Avatar, Button, Divider, Flex, Tag, Tooltip, Typography, theme } from 'antd';
import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { getCommentContent, isEditorBoxEmpty } from '../../../utils/common';
import { DocumentReactions, AddEmojiButton } from './DocumentReactions';
import { EMOJI_MAP } from 'editor/src/components/EmojiPicker/data';
import { CmsEditor } from 'editor/src/components/Editor/CmsEditor';
import { useDispatch, useSelector } from 'react-redux';
import {
  addComment,
  addCommentReaction,
  addReply,
  deleteComment,
  deleteCommentReaction,
  editComment,
  fetchComments,
  fetchReplies,
} from '../../../features/editior/editorSlice';
import ReactTimeago from 'react-timeago';
import {
  ArrowUpOutlined,
  CheckOutlined,
  CloseOutlined,
  CommentOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import './index.css';
import { USER_TOP_REACTIONS } from '../../../utils/apiEndpoints';
import clientService from '../../../apiClient';

const { Text } = Typography;
const { useToken } = theme;

const getInitials = (name) => {
  if (name) return String(name).charAt(0).toUpperCase();
  return 'U';
};

export const CommentEditor = React.memo(
  ({ content, editorRef, onSubmit, fileId, workspaceId, user, isReply = false, isEditable = false }) => {
    const [disabled, setDisabled] = useState(true);
    const { token } = useToken();

    return (
      <Flex className="comment-panel-wrapper" style={{ width: '100%' }} gap={token.paddingXS}>
        <Flex vertical align="center" style={{ minWidth: 24, maxWidth: 24, alignSelf: 'stretch' }}>
          <Flex style={{ minHeight: token.paddingSM }}>
            {isReply && (
              <Divider
                style={{
                  height: '100%',
                  borderInlineStartColor: token.colorBorder,
                  borderInlineStartWidth: '1px',
                }}
                type="vertical"
              />
            )}
          </Flex>
          <Avatar
            style={{
              minWidth: 24,
              maxWidth: 24,
              minHeight: 24,
              maxHeight: 24,
            }}
          >
            {getInitials(user?.name)}
          </Avatar>
        </Flex>
        <Flex
          className="comment-editor-wrapper"
          vertical
          style={{ width: '100%', paddingTop: isReply ? token.paddingSM : 0 }}
          gap={token.paddingXS}
        >
          <Flex className="file-comment-panel" style={{ width: '100%' }}>
            <CmsEditor
              file_id={fileId}
              workspace_id={workspaceId}
              editorRef={editorRef}
              isEditable={isEditable}
              editorContent={content}
              disableRichFeatures
              lastSavedContentRef={null}
              autoSave={false}
              user={user}
              onChange={(value) => isEditorBoxEmpty(value, setDisabled)}
            />
          </Flex>
          <Flex className="comment-panel-footer" align="flex-start" justify="flex-end">
            {/* <Button
              type="text"
              icon={
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <g id="weui:at-outlined">
                    <path
                      id="Vector"
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M9.3917 6.82464C7.97503 6.82464 7.05753 8.02464 7.05753 9.8713C7.05753 11.7071 7.96586 12.8988 9.3817 12.8988C10.8267 12.8988 11.7542 11.6871 11.7542 9.82213C11.7542 7.9863 10.8459 6.82464 9.3917 6.82464ZM9.7042 1.66797C13.9834 1.66797 17.5167 4.60714 17.5167 8.9338C17.5167 12.0388 16.2275 14.0313 14.0792 14.0313C12.9167 14.0313 11.9984 13.328 11.8525 12.2346H11.7842C11.3442 13.348 10.4359 13.963 9.22586 13.963C7.21336 13.963 5.8467 12.303 5.8467 9.84214C5.8467 7.43964 7.23336 5.77964 9.22503 5.77964C10.3584 5.77964 11.3442 6.39464 11.7059 7.3513H11.7742V5.97464H12.9075V11.5605C12.9075 12.4488 13.3759 13.0355 14.2642 13.0355C15.5634 13.0355 16.4517 11.5413 16.4517 8.9138C16.4517 5.1838 13.5317 2.64714 9.7042 2.64714C5.87586 2.64714 2.74086 5.4388 2.74086 9.81213C2.74086 13.8746 6.08586 16.9705 9.81086 16.9705C11.1292 16.9705 12.3892 16.8046 13.0534 16.5413V17.508C12.2617 17.7813 11.11 17.9371 9.8017 17.9371C5.16503 17.9371 1.6667 14.4396 1.6667 9.7738C1.65934 8.71302 1.86171 7.66125 2.26218 6.67894C2.66266 5.69663 3.25333 4.80317 4.00025 4.0499C4.74717 3.29662 5.63561 2.69841 6.61449 2.28963C7.59337 1.88086 8.64339 1.66959 9.7042 1.66797Z"
                      fill="currentColor"
                      fillOpacity="0.88"
                      stroke="black"
                      strokeOpacity="0.88"
                      strokeWidth="0.5"
                    />
                  </g>
                </svg>
              }
            /> */}
            <Button
              onClick={onSubmit}
              disabled={disabled}
              type="primary"
              icon={<ArrowUpOutlined />}
              style={{ borderRadius: 999999, height: 28, width: 28 }}
            />
          </Flex>
        </Flex>
      </Flex>
    );
  }
);

export const CommentCard = React.memo(
  ({ comment, parentCommentId, canReply = true, user, fileId, workspaceId, topReactions }) => {
    const { token } = useToken();
    const dispatch = useDispatch();
    const replyEditorRef = useRef(null);
    const editEditorRef = useRef(null);
    const [showReply, setShowReply] = useState(false);
    const [editMode, setEditMode] = useState({ prev: false, new: false });
    const [disableEditButton, setDisableEditButton] = useState(true);
    const [reactionOpen, setReactionOpen] = useState(false);
    const [showReplyEditor, setShowReplyEditor] = useState(false);
    const repliesMap = useSelector((state) => state.editor.repliesMap[comment?.id]) || [];
    const commentReactionsLoadingMap = useSelector((state) => state.editor.commentReactionsLoadingMap);
    const repliesLoadingMap = useSelector((state) => state.editor.repliesLoadingMap[comment?.id]);

    const content = getCommentContent(comment?.content);

    const onReactionOpenChange = useCallback((open) => setReactionOpen(open), [setReactionOpen]);

    useEffect(() => {
      editEditorRef.current?.setContent(content);
    }, [comment?.content]);

    useEffect(() => {
      if (!editMode?.new && editMode?.prev !== editMode?.new && editEditorRef.current) {
        editEditorRef.current?.setContent(content);
      }
    }, [editMode, editEditorRef, content]);

    const onSubmitHandler = useCallback(async () => {
      if (!replyEditorRef.current) return;

      const content = replyEditorRef.current.getJSON();
      if (!content || Object.keys(content).length === 0) return;

      try {
        await dispatch(
          addReply({
            name: user?.name,
            commentId: comment?.id,
            content: JSON.stringify(content),
            workspace_id: workspaceId,
          })
        ).unwrap();

        replyEditorRef.current.setContent('');
        setShowReplyEditor(false);
        setShowReply(true);
      } catch (error) {
        console.error('Error adding reply:', error);
      }
    }, [workspaceId, user, comment?.id, replyEditorRef, dispatch]);

    const handleLoadReplies = async () => {
      try {
        if (!repliesMap.length) {
          await dispatch(fetchReplies({ commentId: comment?.id, workspace_id: workspaceId })).unwrap();
        }
        setShowReply(true);
      } catch (error) {
        console.error('Error loading replies:', error);
      }
    };

    const handleEditComment = async () => {
      if (!editEditorRef.current) return;
      const content = editEditorRef.current.getJSON();
      if (!content || Object.keys(content).length === 0) return;
      try {
        dispatch(
          editComment({
            commentId: comment.id,
            content: JSON.stringify(content),
            workspace_id: workspaceId,
            isReply: !!parentCommentId,
            parentCommentId,
          })
        );
        setEditMode({ prev: true, new: false });
      } catch (error) {
        console.error('Error editing comment:', error);
      }
    };

    const handleDelete = async ({ commentId }) => {
      try {
        await dispatch(deleteComment({ commentId, workspace_id: workspaceId, username: user?.username })).unwrap();
      } catch (error) {}
    };

    const reactionClickHandler = useCallback(
      async ({ reaction }) => {
        try {
          const hasReacted = comment?.reactions?.[reaction]?.users?.some((u) => u.username === user?.username);
          if (hasReacted) {
            await dispatch(
              deleteCommentReaction({
                commentId: comment.id,
                reaction: reaction,
                username: user?.username,
                workspace_id: workspaceId,
                isReply: !!parentCommentId,
                parentCommentId,
              })
            ).unwrap();
          } else {
            dispatch(
              addCommentReaction({
                commentId: comment.id,
                reaction: reaction,
                workspace_id: workspaceId,
                isReply: !!parentCommentId,
                parentCommentId,
                name: user?.name,
                username: user?.username,
              })
            ).unwrap();
          }
        } catch (error) {
          console.error('Error handling reaction:', error);
        } finally {
          setReactionOpen(false);
        }
      },
      [comment, user, workspaceId, parentCommentId]
    );

    const hasReactions = useMemo(
      () => comment?.reactions && Object.entries(comment.reactions)?.length > 0,
      [comment.reactions]
    );

    return (
      <>
        <Flex
          id={comment?.id}
          vertical
          style={{ backgroundColor: token.colorBgContainer, width: '100%' }}
          className="comment-card-wrapper"
        >
          {/* header */}
          <Flex className="comment-card-layout" align="flex-start" flex={1}>
            <Flex vertical align="center" style={{ minWidth: 24, maxWidth: 24, alignSelf: 'stretch' }}>
              <Flex style={{ minHeight: token.paddingSM + 1 }}>
                <Divider
                  style={{
                    height: '100%',
                    borderInlineStartColor: parentCommentId ? token.colorBorder : token.colorBgContainer,
                    borderInlineStartWidth: '1px',
                  }}
                  type="vertical"
                />
              </Flex>
              <Avatar
                style={{
                  minWidth: 24,
                  maxWidth: 24,
                  minHeight: 24,
                  maxHeight: 24,
                }}
              >
                {getInitials(comment?.name)}
              </Avatar>
              {parentCommentId && (
                <Divider
                  style={{ height: '100%', borderInlineStartColor: token.colorBorder, borderInlineStartWidth: '1px' }}
                  type="vertical"
                />
              )}
            </Flex>
            <Flex
              className="comment-panel-header-wrapper"
              vertical
              align="flex-start"
              flex={1}
              style={{ paddingTop: token.paddingSM, width: '100%' }}
            >
              <Flex
                className="comment-panel-header"
                style={{ paddingLeft: token.paddingSM, paddingRight: token.paddingSM, minHeight: 28, width: '100%' }}
                gap={token.paddingXS}
                align="center"
                justify="flex-start"
              >
                <Text style={{ fontWeight: token.fontWeightStrong }}>{comment?.name}</Text>
                <Flex style={{ color: token.colorTextSecondary, fontSize: token.fontSize }}>
                  <Flex style={{ fontSize: token.fontSize - 1, color: token.colorTextLabel }}>
                    <ReactTimeago
                      date={new Date(comment?.created_at)}
                      formatter={(value, unit) => {
                        if (unit === 'second') return `just now`;
                        return `${value} ${unit} ago`;
                      }}
                    />
                  </Flex>
                </Flex>
                {comment?.created_by === user?.username && (
                  <Flex className={`comment-panel-actions`}>
                    <Tooltip title="Edit comment" styles={{ body: { fontSize: '12px' } }}>
                      <Button
                        style={{ width: 'unset', padding: 0, height: 'unset' }}
                        type="text"
                        icon={<EditOutlined style={{ fontSize: '16px', color: token.colorTextSecondary }} />}
                        onClick={() => setEditMode({ prev: false, new: true })}
                      />
                    </Tooltip>
                    <Tooltip title="Delete comment" styles={{ body: { fontSize: '12px' } }}>
                      <Button
                        style={{ width: 'unset', padding: 0, height: 'unset' }}
                        type="text"
                        icon={<DeleteOutlined style={{ fontSize: '16px', color: token.colorTextSecondary }} />}
                        onClick={() => {
                          handleDelete({ commentId: comment.id });
                        }}
                      />
                    </Tooltip>
                  </Flex>
                )}
              </Flex>
              {/* body */}
              <Flex className={`comment-box reply ${editMode?.new ? 'edit' : ''}`} style={{ width: '100%' }}>
                <CmsEditor
                  editorRef={editEditorRef}
                  isEditable={editMode?.new}
                  editorContent={{ data: content }}
                  user={user}
                  file_id={fileId}
                  workspace_id={workspaceId}
                  autoSave={false}
                  lastSavedContentRef={null}
                  onChange={(val) => isEditorBoxEmpty(val, setDisableEditButton)}
                  disableRichFeatures={true}
                />
                {editMode?.new && (
                  <Flex gap={token.paddingXXS}>
                    <Button
                      type="text"
                      style={{ borderRadius: 99999, minWidth: 28, maxWidth: 28, minHeight: 28, maxHeight: 28 }}
                      onClick={() => setEditMode({ prev: true, new: false })}
                      icon={<CloseOutlined style={{ fontSize: 12 }} />}
                    />
                    <Button
                      type="primary"
                      disabled={disableEditButton}
                      style={{ borderRadius: 99999, minWidth: 28, maxWidth: 28, minHeight: 28, maxHeight: 28 }}
                      icon={<CheckOutlined style={{ fontSize: 12 }} />}
                      onClick={handleEditComment}
                    />
                  </Flex>
                )}
              </Flex>
              {hasReactions && (
                <Flex
                  style={{ padding: token.paddingSM, paddingTop: token.paddingXS, paddingBottom: token.paddingXS }}
                  gap={token.paddingXXS}
                  align="center"
                  wrap="wrap"
                >
                  {Object.entries(comment?.reactions || {}).map(([reaction, data]) => (
                    <Tag
                      color="processing"
                      onClick={() => reactionClickHandler({ reaction })}
                      style={{ cursor: 'pointer', borderRadius: '999px' }}
                    >
                      {commentReactionsLoadingMap[comment.id] && <SyncOutlined spin />}
                      <Text style={{ fontSize: token.fontSize, color: 'inherit' }}>
                        {EMOJI_MAP[reaction] || reaction} {data.count}
                      </Text>
                    </Tag>
                  ))}
                </Flex>
              )}
              {!editMode?.new && (
                <Flex
                  align="center"
                  gap={token.paddingSM}
                  style={{ paddingLeft: token.paddingSM, paddingRight: token.paddingSM }}
                >
                  {canReply && (
                    <Button
                      type="link"
                      style={{ paddingLeft: 0, paddingRight: 0 }}
                      onClick={() => setShowReplyEditor(true)}
                    >
                      <Flex align="center" gap={token.paddingXXS}>
                        <CommentOutlined style={{ fontSize: token.fontSizeLG }} />
                        <Text style={{ color: 'inherit' }}>Reply</Text>
                      </Flex>
                    </Button>
                  )}
                  {canReply && comment?.has_replies ? (
                    !showReply ? (
                      <Button
                        loading={repliesLoadingMap}
                        type="link"
                        onClick={handleLoadReplies}
                        style={{ paddingLeft: 0, paddingRight: 0 }}
                      >
                        <Flex align="center" gap={token.paddingXXS}>
                          <EyeOutlined style={{ fontSize: token.fontSizeLG }} />
                          <Text style={{ color: 'inherit' }}>Show Reply</Text>
                        </Flex>
                      </Button>
                    ) : (
                      <Button
                        type="link"
                        onClick={() => setShowReply(false)}
                        style={{ paddingLeft: 0, paddingRight: 0 }}
                      >
                        <Flex align="center" gap={token.paddingXXS}>
                          <EyeInvisibleOutlined style={{ fontSize: token.fontSizeLG }} />
                          <Text style={{ color: 'inherit' }}>Hide Reply</Text>
                        </Flex>
                      </Button>
                    )
                  ) : null}
                  <AddEmojiButton
                    topReactions={topReactions}
                    onClick={(value) => reactionClickHandler({ reaction: value?.code })}
                    open={reactionOpen}
                    onOpenChange={onReactionOpenChange}
                  />
                </Flex>
              )}
            </Flex>
          </Flex>
        </Flex>
        {showReply && (
          <Flex vertical style={{ width: '100%' }} className="replies-section">
            {repliesMap.map((reply) => (
              <CommentCard
                key={reply?.id}
                comment={reply}
                parentCommentId={comment?.id}
                canReply={true}
                user={user}
                topReactions={topReactions}
              />
            ))}
          </Flex>
        )}

        {showReplyEditor && (
          <Flex className="reply-comment-wrapper" style={{ width: '100%' }}>
            <CommentEditor
              user={user}
              fileId={fileId}
              workspaceId={workspaceId}
              onSubmit={onSubmitHandler}
              editorRef={replyEditorRef}
              showDividers={true}
              isReply={true}
              isEditable={true}
            />
          </Flex>
        )}
      </>
    );
  }
);

export const DocumentComments = React.memo(() => {
  const { token } = useToken();
  const dispatch = useDispatch();
  const commentEditorRef = useRef(null);
  const { user } = useSelector((state) => state.user);
  const { activeFile } = useSelector((state) => state.organization);
  const { comments, totalComments } = useSelector((state) => state.editor);
  const [topReactions, setTopReactions] = useState([]);

  useEffect(() => {
    const file_id = activeFile?.id;
    const workspace_id = activeFile?.workspace_id;
    dispatch(fetchComments({ file_id, workspace_id: workspace_id }));
    clientService
      .get(`${USER_TOP_REACTIONS}`)
      .then((res) => {
        setTopReactions(res?.data || []);
      })
      .catch((err) => console.log(err));
  }, [dispatch, activeFile?.id]);

  const onSubmitHandler = useCallback(async () => {
    if (!commentEditorRef.current) return;
    const content = commentEditorRef.current.getJSON();
    if (!content || Object.keys(content).length === 0) return;
    try {
      dispatch(
        addComment({
          content: JSON.stringify(content),
          name: user?.name,
          workspace_id: activeFile?.workspace_id,
          file_id: activeFile?.id,
        })
      );
      commentEditorRef.current.setContent('');
    } catch (error) {
      console.error('Error adding reply:', error);
    }
  }, [dispatch, user?.name, activeFile?.id]);

  return (
    <>
      <Flex
        vertical
        style={{
          paddingBottom: token.paddingLG,
          paddingLeft: token.paddingSM,
          paddingRight: token.paddingSM,
          marginTop: token.marginSM,
          width: '100%',
        }}
        gap={token.padding}
      >
        {/* reactions panel */}
        <DocumentReactions
          user={user}
          fileId={activeFile?.id}
          workspaceId={activeFile?.workspace_id}
          topReactions={topReactions}
        />
        {/* comments panel for replies */}
        {totalComments > 0 && (
          <Flex align="center" justify="flex-start" gap={token.paddingXS}>
            <CommentOutlined style={{ fontSize: 20 }} />
            <Text style={{ fontWeight: token.fontWeightStrong }}>{totalComments} page comments</Text>
          </Flex>
        )}
        <Flex vertical className="comments-section">
          {comments?.map((comment) => (
            <CommentCard
              key={comment?.id}
              comment={comment}
              parentCommentId={null}
              user={user}
              fileId={activeFile?.id}
              workspaceId={activeFile?.workspace_id}
              folderId={activeFile?.folder_id}
              topReactions={topReactions}
            />
          ))}
        </Flex>

        {/* comment editor */}
        <CommentEditor
          user={user}
          fileId={activeFile?.id}
          workspaceId={activeFile?.workspace_id}
          onSubmit={onSubmitHandler}
          editorRef={commentEditorRef}
          isEditable={true}
        />
      </Flex>
    </>
  );
});

export default DocumentComments;
